import React, { useState } from 'react';
import { ExternalLink, CheckCircle, AlertTriangle, Clock, Search, Loader2, ChevronDown, ChevronUp, Share2, Link, Copy, Check, Image, Info, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import apiService from '../services/api';
import { useBrandSettings } from '../context/BrandSettingsContext';
import { toast } from 'sonner';
import CompetingForcesCard from './CompetingForcesCard';

const ResultCard = ({ result }) => {
  const { getLogoForVerdict, getVerdictClass, tagline } = useBrandSettings();
  const [deepDiveResults, setDeepDiveResults] = useState(null);
  const [isLoadingDeepDive, setIsLoadingDeepDive] = useState(false);
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [deepDiveError, setDeepDiveError] = useState(null);
  const [copied, setCopied] = useState(false);

  const getVeracityColor = (veracity) => {
    const cls = getVerdictClass(veracity);
    if (cls === 'true') return 'bg-green-100 text-green-800 border-green-300';
    if (cls === 'false') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  };

  const getVeracityIcon = (veracity) => {
    const logo = getLogoForVerdict(veracity);
    if (logo) {
      return <img src={logo} alt={veracity} className="w-6 h-6 rounded object-contain" />;
    }
    // Fallback icons if no brand logo set
    const cls = getVerdictClass(veracity);
    if (cls === 'true') return <CheckCircle className="w-6 h-6 text-green-600" />;
    if (cls === 'false') return <div className="w-6 h-6 flex items-center justify-center"><div className="w-4 h-4 bg-red-600 rounded-full"></div></div>;
    return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
  };

  const getVerdictEmoji = (veracity) => {
    const cls = getVerdictClass(veracity);
    if (cls === 'true') return '✅';
    if (cls === 'false') return '❌';
    return '⚠️';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Helper to display claim - handle image uploads cleanly
  const getDisplayClaim = (claim, claimType) => {
    if (claimType === 'image' || (claim && claim.startsWith('data:image'))) {
      return 'Image Analysis';
    }
    // Truncate very long claims
    if (claim && claim.length > 200) {
      return claim.substring(0, 200) + '...';
    }
    return claim;
  };

  // Check if claim is an image
  const isImageClaim = result.claim_type === 'image' || (result.claim && result.claim.startsWith('data:image'));

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Just now';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Just now';
    }
  };

  // Share functionality
  const getShareText = () => {
    const verdict = result.veracity;
    const confidence = result.confidence;
    const claimText = isImageClaim ? 'an image claim' : `"${result.claim?.substring(0, 100)}${result.claim?.length > 100 ? '...' : ''}"`;
    return `KaiCheckIt verification result on ${claimText}: ${verdict} (${confidence}% confidence). ${tagline}`;
  };

  const getShareUrl = () => {
    // Use the fact_check_id to create a shareable public URL
    const baseUrl = window.location.origin;
    return `${baseUrl}/result/${result.fact_check_id}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareWhatsApp = () => {
    // Put URL first so WhatsApp generates rich preview, then add context
    const baseUrl = window.location.origin;
    // Cache-buster forces WhatsApp to re-crawl instead of showing a stale cached preview
    const ogUrl = `${baseUrl}/api/share/og/${result.fact_check_id}?v=${Date.now()}`;
    const emoji = getVerdictEmoji(result.veracity);
    // URL must be at the end for WhatsApp to generate preview
    const text = encodeURIComponent(`🛡️ KaiCheckIt verdict: ${emoji} ${result.veracity} (${result.confidence}% confidence)\n\n`);
    const url = encodeURIComponent(ogUrl);
    window.open(`https://wa.me/?text=${text}${url}`, '_blank');
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const handleShareFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  };

  const handleShareLinkedIn = async () => {
    // LinkedIn does not allow pre-filling post text via URL, so we copy it
    // to the clipboard for the user to paste into their post.
    const baseUrl = window.location.origin;
    // Cache-buster forces LinkedIn to re-crawl instead of showing a stale cached preview
    const ogUrl = `${baseUrl}/api/share/og/${result.fact_check_id}?v=${Date.now()}`;
    try {
      const response = await apiService.request(`/share/linkedin/${result.fact_check_id}`);
      const postText = response?.share_text || getShareText();
      await navigator.clipboard.writeText(postText);
      toast.success('Post text copied! Paste it into your LinkedIn post.', { duration: 5000 });
    } catch (err) {
      try {
        await navigator.clipboard.writeText(getShareText());
        toast.success('Post text copied! Paste it into your LinkedIn post.', { duration: 5000 });
      } catch {
        // Clipboard unavailable - continue to share anyway
      }
    }
    const encodedUrl = encodeURIComponent(ogUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank');
  };

  const handleDeepDive = async () => {
    if (deepDiveResults) {
      setShowDeepDive(!showDeepDive);
      return;
    }

    try {
      setIsLoadingDeepDive(true);
      setDeepDiveError(null);
      
      // For image claims, use the summary instead of the base64 data
      let searchQuery = result.claim;
      if (isImageClaim) {
        // Extract key terms from the summary for searching
        // Take first 200 chars of summary as search query
        searchQuery = result.summary ? result.summary.substring(0, 200) : 'image verification';
      }
      
      const response = await apiService.searchFactChecks(searchQuery);
      setDeepDiveResults(response);
      setShowDeepDive(true);
    } catch (error) {
      console.error('Deep dive error:', error);
      setDeepDiveError('Unable to search Fact-Checker database. Please try again.');
    } finally {
      setIsLoadingDeepDive(false);
    }
  };

  return (
    <Card className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-300 dark:hover:border-blue-600 overflow-hidden bg-white dark:bg-slate-800 relative">
      {/* Share Button - Fixed to upper right */}
      <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="bg-white dark:bg-slate-800 shadow-sm">
              <Share2 className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Share</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Link className="w-4 h-4 mr-2" />
                  Copy Link
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleShareWhatsApp} className="cursor-pointer">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleShareTwitter} className="cursor-pointer">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              X (Twitter)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleShareFacebook} className="cursor-pointer">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleShareLinkedIn} className="cursor-pointer">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CardHeader className="p-4 sm:p-6 pr-20 sm:pr-24">
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="mt-1 flex-shrink-0">{getVeracityIcon(result.veracity)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {isImageClaim && (
                <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700">
                  <Image className="w-3 h-3 mr-1" />
                  Image Analysis
                </Badge>
              )}
            </div>
            <CardTitle className="text-base sm:text-xl mb-2 text-slate-800 dark:text-slate-100 break-words whitespace-normal overflow-wrap-anywhere" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              {getDisplayClaim(result.claim, result.claim_type)}
            </CardTitle>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <Badge className={`${getVeracityColor(result.veracity)} font-semibold text-xs sm:text-sm px-2 sm:px-3 py-0.5 sm:py-1`}>
                {result.veracity}
              </Badge>
              <Badge variant="outline" className="text-xs dark:text-slate-300 dark:border-slate-600">
                {result.category}
              </Badge>
              <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                <Clock className="w-3 h-3" />
                <span className="hidden sm:inline">{formatDate(result.timestamp || result.created_at)}</span>
                <span className="sm:hidden">{new Date(result.timestamp || result.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      
      {/* Display uploaded image if present */}
      {(result.image_url || isImageClaim) && (
        <div className="px-4 sm:px-6 pb-2">
          <div className="relative rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
            {result.image_url ? (
              <img 
                src={result.image_url} 
                alt="Analyzed image"
                className="w-full max-h-64 object-contain"
              />
            ) : isImageClaim && result.claim?.startsWith('data:image') ? (
              <img 
                src={result.claim} 
                alt="Analyzed image"
                className="w-full max-h-64 object-contain"
              />
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-400 dark:text-slate-500">
                <Image className="w-8 h-8 mr-2" />
                <span className="text-sm">Image analyzed</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
        {/* Breaking News + Real-time Context Indicator */}
        {(result.has_realtime_context || result.is_breaking_news) && (
          <div className="flex flex-wrap items-center gap-2 p-2 sm:p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/30 dark:to-green-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
            <div className="flex items-center gap-2 flex-wrap">
              {result.is_breaking_news && (
                <Badge className="bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700 text-xs">
                  <Zap className="w-3 h-3 mr-1" />
                  Breaking News
                </Badge>
              )}
              {result.has_realtime_context && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-700 dark:text-green-400 font-medium">
                    Verified with real-time web search
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200">Confidence Score</span>
              {result.confidence_explanation && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs p-3 bg-slate-900 text-white">
                      <p className="text-sm font-medium mb-1">Why this confidence level?</p>
                      <p className="text-xs text-slate-300">{result.confidence_explanation}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <span className={`text-xs sm:text-sm font-bold ${getConfidenceColor(result.confidence)}`}>
              {result.confidence}%
            </span>
          </div>
          <Progress value={result.confidence} className="h-2" />
          
          {/* Confidence explanation for breaking news */}
          {result.confidence_explanation && result.is_breaking_news && (
            <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-800 dark:text-amber-300">
              <span className="font-medium">📊 Why not 100%?</span> {result.confidence_explanation}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Analysis Summary</h4>
          <CardDescription className="text-slate-600 dark:text-slate-300 leading-relaxed text-xs sm:text-sm">
            {result.summary}
          </CardDescription>
        </div>

        {/* Competing Forces (AI-triggered analytical layer) — sits between Analysis Summary and Sources */}
        {result.competing_forces?.triggered === true && (
          <CompetingForcesCard forces={result.competing_forces} />
        )}

        {result.sources && result.sources.length > 0 && (
          <div>
            <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Sources</h4>
            <div className="space-y-2">
              {result.sources.map((source, index) => (
                <a
                  key={index}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200 group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                      {source.name}
                    </span>
                  </div>
                  <Badge variant="secondary" className="text-xs ml-2 flex-shrink-0">
                    {source.credibility}
                  </Badge>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Web Search Results - Real-time News Sources */}
        {result.web_sources && result.web_sources.length > 0 && (
          <div>
            <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              Real-time News Sources
            </h4>
            <div className="space-y-2">
              {result.web_sources.map((source, index) => (
                <a
                  key={index}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors duration-200 group border border-blue-100 dark:border-blue-800"
                >
                  <div className="flex items-start gap-2">
                    <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors block truncate">
                        {source.title}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 block mt-0.5">
                        {source.source}
                      </span>
                      {source.snippet && (
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2 hidden sm:block">
                          {source.snippet}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Deep Dive Section - Available to ALL users */}
        <div className="pt-3 sm:pt-4 border-t border-slate-200 dark:border-slate-700">
          <Button
            onClick={handleDeepDive}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-600 text-xs sm:text-sm"
            disabled={isLoadingDeepDive}
          >
            {isLoadingDeepDive ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Searching fact-checkers...</span>
                <span className="sm:hidden">Searching...</span>
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Explore Other FactCheckers</span>
                <span className="sm:hidden">Explore FactCheckers</span>
                {deepDiveResults && (showDeepDive ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />)}
              </>
            )}
          </Button>

          {deepDiveError && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2 text-center">{deepDiveError}</p>
          )}

          {showDeepDive && deepDiveResults && (
            <div className="mt-3 sm:mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Related Verifications Found
                </h4>
                <Badge variant="outline" className="text-xs dark:text-slate-300 dark:border-slate-600">
                  {deepDiveResults.total_found} found
                </Badge>
              </div>
              
              {deepDiveResults.results.length > 0 ? (
                <div className="space-y-3">
                  {deepDiveResults.results.map((item, index) => (
                    <div key={index} className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 sm:p-4 border border-blue-100 dark:border-blue-800">
                      <p className="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-100 mb-2">
                        "{item.claim.text}"
                      </p>
                      {item.claim.claimant && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                          Claimed by: {item.claim.claimant}
                        </p>
                      )}
                      <div className="space-y-2">
                        {item.reviews.map((review, rIndex) => (
                          <a
                            key={rIndex}
                            href={review.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border border-blue-100 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors group"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
                                {review.publisher}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {review.title}
                              </p>
                            </div>
                            <Badge 
                              className={`ml-2 text-xs flex-shrink-0 ${
                                review.textual_rating.toLowerCase().includes('true') || review.textual_rating.toLowerCase().includes('correct')
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                                  : review.textual_rating.toLowerCase().includes('false') || review.textual_rating.toLowerCase().includes('wrong')
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                              }`}
                            >
                              {review.textual_rating}
                            </Badge>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                    {deepDiveResults.message}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Kai's analysis above is your primary source for this claim.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
      
      {/* Viral Share Prompt */}
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-center sm:text-left">
              <span className="text-lg">🛡️</span>
              <p className="text-sm text-slate-700 dark:text-slate-200">
                <span className="font-semibold">Help fight misinformation</span>
                <span className="text-slate-500 dark:text-slate-400"> — share this result!</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShareWhatsApp}
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/30"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShareLinkedIn}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShareTwitter}
                className="text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ResultCard;